import React, { useState, useEffect } from 'react';
import MonthPicker from '../../molecules/profile/components/MonthPicker';
import '../../../styles/resume-editor/edit-experience-form.css';

interface CustomSectionEntry {
  title: string;
  description?: string;
  date?: Date | null;
  endDate?: Date | null;
  yearOnly?: boolean;
  hideDate?: boolean;
  hideEndDate?: boolean;
}

interface AddCustomSectionEntryProps {
  sectionTitle: string;
  initialEntry?: CustomSectionEntry;
  onSave: (entry: CustomSectionEntry) => void;
  onCancel: () => void;
}

export const AddCustomSectionEntry: React.FC<AddCustomSectionEntryProps> = ({
  sectionTitle,
  initialEntry,
  onSave,
  onCancel,
}) => {
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [date, setDate] = useState<Date | null>(null);
  const [endDate, setEndDate] = useState<Date | null>(null);
  const [showDateCalendar, setShowDateCalendar] = useState(false);
  const [showEndDateCalendar, setShowEndDateCalendar] = useState(false);
  const [yearOnly, setYearOnly] = useState(false);
  const [hideDate, setHideDate] = useState(false);
  const [hideEndDate, setHideEndDate] = useState(false);

  useEffect(() => {
    if (initialEntry) {
      setTitle(initialEntry.title || '');
      setDescription(initialEntry.description || '');
      setDate(initialEntry.date || null);
      setEndDate(initialEntry.endDate || null);
      setYearOnly(initialEntry.yearOnly || false);
      setHideDate(initialEntry.hideDate || false);
      setHideEndDate(initialEntry.hideEndDate || false);
    }
  }, [initialEntry]);

  const formatDate = (date: Date | null): string => {
    if (!date) return '+ select date';
    if (yearOnly) {
      return date.getFullYear().toString();
    }
    const month = date.toLocaleDateString('en-US', { month: 'short' });
    const year = date.getFullYear();
    return `${month} ${year}`;
  };

  const handleSave = () => {
    if (!title.trim()) {
      return;
    }

    const entry: CustomSectionEntry = {
      title: title.trim(),
      description: description.trim() || undefined,
      date: date || undefined,
      endDate: endDate || undefined,
      yearOnly,
      hideDate,
      hideEndDate,
    };

    onSave(entry);
  };

  return (
    <div className="edit-experience-form">
      <div className="edit-experience-form-content">
        <div className="edit-experience-form-field">
          <label className="edit-experience-form-label" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <span>{sectionTitle}</span>
            <span style={{ color: '#9ca3af', fontSize: '10px', fontWeight: 'normal' }}>Required</span>
          </label>
          <input
            type="text"
            className="edit-experience-form-input"
            placeholder={`Enter ${sectionTitle.toLowerCase()}`}
            value={title}
            onChange={(e) => setTitle(e.target.value)}
          />
        </div>

        <div className="edit-experience-form-field">
          <label className="edit-experience-form-label">Description</label>
          <textarea
            className="edit-experience-form-textarea"
            placeholder="Enter description"
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            rows={3}
          />
        </div>

        <div className="edit-experience-form-field">
          <div className="edit-experience-form-date-wrapper">
            <label className="edit-experience-form-label">Date</label>
            <button
              type="button"
              className="edit-experience-form-date-btn"
              onClick={() => {
                setShowDateCalendar(!showDateCalendar);
                setShowEndDateCalendar(false);
              }}
              disabled={hideDate}
            >
              {formatDate(date)}
            </button>
            {showDateCalendar && (
              <div className="edit-experience-form-month-picker-wrapper">
                <MonthPicker
                  selectedDate={date}
                  onSelect={(selectedDate) => {
                    setDate(selectedDate);
                    setShowDateCalendar(false);
                  }}
                  onClose={() => setShowDateCalendar(false)}
                />
              </div>
            )}
          </div>
          {!hideEndDate && (
            <div className="edit-experience-form-date-wrapper" style={{ marginTop: '8px' }}>
              <label className="edit-experience-form-label">End Date</label>
              <button
                type="button"
                className="edit-experience-form-date-btn"
                onClick={() => {
                  setShowEndDateCalendar(!showEndDateCalendar);
                  setShowDateCalendar(false);
                }}
                disabled={hideDate}
              >
                {formatDate(endDate)}
              </button>
              {showEndDateCalendar && (
                <div className="edit-experience-form-month-picker-wrapper edit-experience-form-month-picker-wrapper--end-date">
                  <MonthPicker
                    selectedDate={endDate}
                    onSelect={(selectedEndDate) => {
                      setEndDate(selectedEndDate);
                      setShowEndDateCalendar(false);
                    }}
                    onClose={() => setShowEndDateCalendar(false)}
                    minDate={date || undefined}
                  />
                </div>
              )}
            </div>
          )}
          <div className="edit-experience-form-date-toggles">
            <label className="edit-experience-form-toggle-label">
              <span>Year only</span>
              <div
                className={`edit-experience-form-toggle-switch ${yearOnly ? 'edit-experience-form-toggle-switch--active' : ''}`}
                onClick={() => setYearOnly(!yearOnly)}
              >
                <div className="edit-experience-form-toggle-switch-thumb" />
              </div>
            </label>
            <label className="edit-experience-form-toggle-label">
              <span>Hide</span>
              <div
                className={`edit-experience-form-toggle-switch ${hideDate ? 'edit-experience-form-toggle-switch--active' : ''}`}
                onClick={() => setHideDate(!hideDate)}
              >
                <div className="edit-experience-form-toggle-switch-thumb" />
              </div>
            </label>
            <label className="edit-experience-form-toggle-label">
              <span>Hide end date</span>
              <div
                className={`edit-experience-form-toggle-switch ${hideEndDate ? 'edit-experience-form-toggle-switch--active' : ''}`}
                onClick={() => setHideEndDate(!hideEndDate)}
              >
                <div className="edit-experience-form-toggle-switch-thumb" />
              </div>
            </label>
          </div>
        </div>
      </div>

      <div className="edit-experience-form-footer">
        <button
          type="button"
          className="edit-experience-form-btn edit-experience-form-btn--cancel"
          onClick={onCancel}
        >
          Cancel
        </button>
        <button
          type="button"
          className="edit-experience-form-btn edit-experience-form-btn--update"
          onClick={handleSave}
          disabled={!title.trim()}
        >
          {initialEntry ? 'Update' : 'Add'}
        </button>
      </div>
    </div>
  );
};

